// Universal lens component (moved from Center/Home/OsintMap in the Phase 8
// universalization — see GS_GRAPH_DESIGN.md §11). Consumed by the MAP and the
// GS GRAPH; change with both pages in mind. Data helpers still live in the
// OsintMap folder (constants/utils are shared by other map components).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Lock, Pause, Play, RotateCcw, X } from "lucide-react";
import {
  TIME_RANGES,
  TIMELINE_MAGNIFY_BOOST,
  TIMELINE_MAGNIFY_RADIUS,
  TIMELINE_PLAYBACK_DURATION_MS,
} from "../../Center/Home/OsintMap/constants";
import {
  displayText,
  filterEventsByTimeRange,
  formatTimestamp,
  getEventId,
  sortByTimeAscending,
} from "../../Center/Home/OsintMap/utils";
import useDark from "../../Center/Graph/hooks/useDark";
import { useLens } from "./LensProvider";

const cyan = "14,165,233";
const MAX_TIMELINE_DOTS = 500;
// Range presets shown on the strip (T4): reconciled with TIME_RANGES. "all" is
// available via the config but omitted here to avoid unbounded fetches — add it back
// if wanted. Order follows TIME_RANGES (1h → 30d).
const TIMELINE_RANGE_KEYS = ["1h", "24h", "7d", "30d"];
// Below this many events the strip shows individual dots; at/above it switches to a
// density histogram so dense windows stay legible instead of an overlapping pile (T3).
const TIMELINE_DOT_MODE_MAX = 48;
const TIMELINE_HISTOGRAM_BUCKETS = 56;
// Playback speed multipliers cycled by the speed button (T1).
const TIMELINE_PLAYBACK_SPEEDS = [0.5, 1, 2];
// ── the brush (§48.1) ───────────────────────────────────────────────────────
// The brush IS the visible time slice and the playhead generalized to a range;
// the provider owns the value, this file owns the gesture. Widths are a % of
// the loaded span so the instrument feels the same at 1h and at 30d.
const BRUSH_MIN_PCT = 2; // drag floor — the provider allows zero width, a hand does not
const BRUSH_DEFAULT_PCT = 10; // click-to-create width
const BRUSH_GRIP_PX = 9; // edge hit area (each side)
// The readout lane floats OUTSIDE the card's box (`bottom-full mb-2 h-5`), so
// it never reaches a measurement of the card — but it does cover the scene, so
// a consumer reserving the strip's band has to be told about it: h-5 + mb-2.
const BRUSH_READOUT_BAND = 28;
// Token-based, both states: "future" (outside the slice) was raw slate-500,
// which sat outside the theme system entirely — mutedText is the app's own
// de-emphasis gray and tracks light mode.
const timelineDotTheme = {
  current: "bg-velBlue",
  future: "bg-mutedText/70",
};

function TimelineHoverTooltip({ event, left, magnified = false }) {
  if (!event) return null;

  const properties = event.properties || {};

  return (
    <div
      className={[
        "pointer-events-none absolute bottom-full z-[2] mb-2 w-64 origin-bottom -translate-x-1/2 overflow-hidden rounded-xl",
        "border border-mainDivider bg-mainBg text-left text-mainText shadow-2xl shadow-black/45",
        "transition-transform",
        magnified ? "scale-[1.03]" : "scale-100",
      ].join(" ")}
      style={{ left: `${left}%` }}
    >
      <div className="border-b border-mainDivider px-3 py-2">
        <div className="mb-1 flex items-center gap-2">
          <span
            className={`h-2.5 w-2.5 shrink-0 rounded-full ${timelineDotTheme.current}`}
          />
          <span className="truncate text-[10px] uppercase tracking-wide text-mutedText">
            {displayText(properties.sourceLabel || properties.source, "OSINT")}
          </span>
        </div>

        <div className="line-clamp-2 text-xs leading-5 text-mainText">
          {displayText(properties.title, "Untitled event")}
        </div>
      </div>

      <div className="px-3 py-2 text-[10px] text-mutedText">
        {displayText(properties.location || properties.category, "Global")} ·{" "}
        {formatTimestamp(properties.timestamp)}
      </div>
    </div>
  );
}

// A hovered histogram bar covers a time window, not a single event — so summarise
// what's in it: the window, the total, and the top event types (with their colours).
const formatBucketRange = (start, end) => {
  if (!Number.isFinite(start) || !Number.isFinite(end)) return "";
  const startDate = new Date(start * 1000);
  const endDate = new Date(end * 1000);
  const spanHours = (end - start) / 3600;
  if (spanHours <= 26) {
    const t = (d) =>
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const day = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${day} · ${t(startDate)} – ${t(endDate)}`;
  }
  const d = (x) =>
    x.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${d(startDate)} – ${d(endDate)}`;
};

function TimelineBucketTooltip({ bucket, left }) {
  if (!bucket) return null;
  const types = bucket.types || [];
  const top = types.slice(0, 4);
  const more = types.length - top.length;

  return (
    <div
      className="pointer-events-none absolute bottom-full z-[2] mb-2 w-60 origin-bottom -translate-x-1/2 overflow-hidden rounded-xl border border-mainDivider bg-mainBg text-left text-mainText shadow-2xl shadow-black/45"
      style={{ left: `${left}%` }}
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-mainDivider px-3 py-2">
        <span className="truncate text-[10px] uppercase tracking-wide text-mutedText">
          {formatBucketRange(bucket.start, bucket.end)}
        </span>
        <span className="shrink-0 text-xs font-semibold text-mainText">
          {bucket.total}
          <span className="ml-1 text-[10px] font-normal text-mutedText">
            {bucket.total === 1 ? "event" : "events"}
          </span>
        </span>
      </div>

      <div className="px-3 py-2">
        {top.length === 0 ? (
          <div className="text-[10px] text-mutedText">
            No type breakdown available.
          </div>
        ) : (
          <div className="space-y-1">
            {top.map((type) => (
              <div
                key={type.key}
                className="flex items-center gap-2 text-[11px]"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: type.color }}
                />
                <span className="min-w-0 flex-1 truncate text-mainText">
                  {type.label}
                </span>
                <span className="shrink-0 tabular-nums text-mutedText">
                  {type.count}
                </span>
              </div>
            ))}
            {more > 0 && (
              <div className="pt-0.5 text-[10px] text-mutedText">
                +{more} more type{more === 1 ? "" : "s"}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const shortRangeDate = (isoDate) => {
  const parsed = Date.parse(`${isoDate || ""}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return "";
  return new Date(parsed).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
};

const isoDateDaysAgo = (days) =>
  new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

const rangeLabel = (key) =>
  TIME_RANGES.find((range) => range.key === key)?.label ||
  (key === "custom" ? "Custom" : displayText(key, ""));

export default function TimelineStrip({
  bottomOffset = 278,
  customRange = null,
  events,
  onCustomRangeApply,
  // Optional: reports this strip's rendered band (card + readout lane) so a
  // consumer can reserve it instead of guessing. A page that lays out around
  // its own strip passes nothing and nothing is measured.
  onHeightChange,
  onPlaybackChange,
  onSelectEvent,
  onTimeRangeChange,
  playbackStopSignal,
  // A GENUINE lock only: the window the caller sends is fixed and the range
  // control would be a lie. Trending is NOT one — it resolves event types and
  // nothing else, so the lens's own window is what filters and it stays
  // editable (lensShellContract.md, THE TRENDING ECHO). The chip states the
  // range actually in force; it never names a preset or a hard-coded span.
  rangeLocked = false,
  selectedEvent,
  serverTimeline = null,
  // Hides the roll transport (play/pause + speed) while KEEPING the brush,
  // the clear-slice button, and the range control. For a scene where a
  // rolling brush has no useful meaning (the paginated feed — its brush
  // requeries on settle, so a roll would fire a query per pause). The brush
  // itself stays fully usable.
  showPlayback = true,
  timeRange,
}) {
  const dark = useDark();
  const customPickerRef = useRef(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const timelineTrackRef = useRef(null);
  const stripRef = useRef(null);

  useEffect(() => {
    if (!customOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!customPickerRef.current) return;
      if (customPickerRef.current.contains(event.target)) return;
      setCustomOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setCustomOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [customOpen]);
  // §48.1 — the roll's frame handle. ZERO IDLE FRAMES: this is non-null only
  // while a roll is running, and the only scheduler is the roll effect below.
  const playbackFrameRef = useRef(null);
  const [hoveredTimelineEvent, setHoveredTimelineEvent] = useState(null);
  const [scrubPercent, setScrubPercent] = useState(null);
  // §48.2 C — the brush lives in the PROVIDER (cross-page continuity); this
  // component is its instrument, not its owner. `isPlaying` is the roll.
  const {
    brush,
    setBrush,
    clearBrush,
    setBrushWindow,
    playbackRolling: isPlaying,
    setPlaybackRolling: setIsPlaying,
    playbackSpeed,
    setPlaybackSpeed,
  } = useLens();
  // The roll reads the live brush without DEPENDING on it — a frame that
  // restarted the effect it runs in would cancel and reschedule 60 times a
  // second and never advance.
  const brushRef = useRef(brush);
  brushRef.current = brush;
  const playheadTimestamp = brush ? brush.to : null;

  const allTimedEvents = useMemo(
    () =>
      sortByTimeAscending(
        events.filter((event) => event.properties?.timestamp),
      ),
    [events],
  );

  const timedEvents = useMemo(
    () =>
      timeRange === "custom"
        ? allTimedEvents
        : filterEventsByTimeRange(allTimedEvents, timeRange),
    [allTimedEvents, timeRange],
  );

  const visibleTimelineEvents = useMemo(() => timedEvents, [timedEvents]);

  const renderedTimelineEvents = useMemo(() => {
    if (visibleTimelineEvents.length <= MAX_TIMELINE_DOTS) {
      return visibleTimelineEvents;
    }

    const sampled = [];
    const step = (visibleTimelineEvents.length - 1) / (MAX_TIMELINE_DOTS - 1);

    for (let index = 0; index < MAX_TIMELINE_DOTS; index += 1) {
      sampled.push(visibleTimelineEvents[Math.round(index * step)]);
    }

    return sampled.filter(Boolean);
  }, [visibleTimelineEvents]);

  // P3-6: a full-range server timeline (density over the whole selected range/all
  // space) takes precedence over the loaded viewport slice.
  const hasServerTimeline = Boolean(serverTimeline?.buckets?.length);
  const hasTimelineEvents = allTimedEvents.length > 0 || hasServerTimeline;
  const timelineDisabled = !hasTimelineEvents;
  const readoutVisible = Boolean(brush) && !timelineDisabled;
  const domainEvents = timedEvents.length > 0 ? timedEvents : allTimedEvents;
  const timestamps = domainEvents.map((event) => event.properties.timestamp);
  const loadedMin = timestamps.length ? Math.min(...timestamps) : 0;
  const loadedMax = timestamps.length ? Math.max(...timestamps) : 0;
  // The axis domain is the server's [start,end] when available (true temporal span),
  // otherwise the loaded-events range. All positioning below derives from min/max.
  const min =
    hasServerTimeline && serverTimeline.start != null
      ? serverTimeline.start
      : loadedMin;
  const max =
    hasServerTimeline && serverTimeline.end != null
      ? serverTimeline.end
      : loadedMax;
  const span = Math.max(1, max - min);
  const selectedId = getEventId(selectedEvent);

  // T3/P3-6: density histogram. Prefer the server's full-range buckets; otherwise
  // bucket the loaded events client-side (used above the dot-mode threshold).
  const histogram = useMemo(() => {
    if (hasServerTimeline) {
      const buckets = serverTimeline.buckets.map((bucket) => bucket.count || 0);
      const bucketCount = buckets.length;
      const start = serverTimeline.start;
      const end = serverTimeline.end;
      return {
        buckets,
        bucketSpan: bucketCount ? (end - start) / bucketCount : 1,
        bucketCount,
        maxCount: Math.max(...buckets, 1),
        fromServer: true,
      };
    }
    if (visibleTimelineEvents.length === 0 || max <= min) return null;
    const bucketCount = TIMELINE_HISTOGRAM_BUCKETS;
    const buckets = new Array(bucketCount).fill(0);
    const bucketSpan = (max - min) / bucketCount;
    visibleTimelineEvents.forEach((event) => {
      const timestamp = event.properties.timestamp;
      let index = Math.floor((timestamp - min) / bucketSpan);
      if (index < 0) index = 0;
      if (index >= bucketCount) index = bucketCount - 1;
      buckets[index] += 1;
    });
    return {
      buckets,
      bucketSpan,
      bucketCount,
      maxCount: Math.max(...buckets, 1),
    };
  }, [hasServerTimeline, serverTimeline, visibleTimelineEvents, min, max]);

  // Per-bucket breakdown for the hover tooltip: total + event-type counts (with
  // colours) from the loaded events, aligned to the histogram's buckets. The server
  // timeline is built from the same features, so these line up with the bars.
  const bucketBreakdowns = useMemo(() => {
    if (!histogram?.bucketCount) return [];
    const acc = Array.from({ length: histogram.bucketCount }, () => ({
      total: 0,
      byType: new Map(),
    }));
    visibleTimelineEvents.forEach((event) => {
      const properties = event.properties || {};
      const timestamp = properties.timestamp;
      if (!Number.isFinite(timestamp)) return;
      let index = Math.floor((timestamp - min) / histogram.bucketSpan);
      if (index < 0) index = 0;
      if (index >= histogram.bucketCount) index = histogram.bucketCount - 1;
      const bucket = acc[index];
      bucket.total += 1;
      const key =
        properties.target_type ||
        properties.category ||
        properties.sourceLabel ||
        "other";
      const existing = bucket.byType.get(key) || {
        key,
        label: displayText(
          properties.sourceLabel || properties.category || key,
          "OSINT",
        ),
        color: properties.color || properties.feedViewColor || "#38bdf8",
        count: 0,
      };
      existing.count += 1;
      bucket.byType.set(key, existing);
    });
    return acc.map((bucket, index) => ({
      start: min + index * histogram.bucketSpan,
      end: min + (index + 1) * histogram.bucketSpan,
      total: bucket.total,
      types: Array.from(bucket.byType.values()).sort(
        (a, b) => b.count - a.count,
      ),
    }));
  }, [visibleTimelineEvents, histogram, min]);

  // The timeline reflects the SELECTED FILTERS, not the viewport (which is only a
  // map rendering/perf concern). So when the server timeline is present it drives
  // the strip: the histogram is the representation. Individual dots (a viewport
  // subset) are only used as a fallback when there's no server timeline.
  const useHistogram =
    Boolean(histogram) &&
    (histogram.fromServer ||
      visibleTimelineEvents.length > TIMELINE_DOT_MODE_MAX);

  const eventLeft = (event) =>
    timedEvents.length === 1
      ? 50
      : ((event.properties.timestamp - min) / span) * 100;

  const nearestEventForPercent = (percent) => {
    if (visibleTimelineEvents.length === 0) return null;

    return visibleTimelineEvents.reduce((nearest, event) => {
      const distance = Math.abs(eventLeft(event) - percent);
      if (!nearest || distance < nearest.distance) return { event, distance };
      return nearest;
    }, null)?.event;
  };

  // ── brush geometry (§48.1) ────────────────────────────────────────────────
  // One conversion pair, used by every gesture and every painted edge.
  const pctOfTs = useCallback(
    (ts) => Math.min(100, Math.max(0, ((ts - min) / span) * 100)),
    [min, span],
  );
  const tsOfPct = useCallback((pct) => min + (span * pct) / 100, [min, span]);
  const brushFromPct = brush ? pctOfTs(brush.from) : null;
  const brushToPct = brush ? pctOfTs(brush.to) : null;
  // The slice test every mark on the strip answers to: no brush = whole window
  // visible (the degenerate state), so nothing dims until a slice exists.
  const inSlice = (ts) =>
    !brush || !Number.isFinite(ts) || (ts >= brush.from && ts <= brush.to);

  const playbackPercent = brushToPct;

  const currentPlaybackEvent =
    playbackPercent === null ? null : nearestEventForPercent(playbackPercent);

  const currentPlaybackEventId = getEventId(currentPlaybackEvent);
  const activeTimelineEvent = hoveredTimelineEvent || currentPlaybackEvent;
  const activeTimelineEventId = getEventId(activeTimelineEvent);
  const hoveredTimestamp = activeTimelineEvent?.properties?.timestamp;

  const hoverLeft =
    scrubPercent ??
    playbackPercent ??
    (activeTimelineEvent
      ? timedEvents.length === 1
        ? 50
        : ((hoveredTimestamp - min) / span) * 100
      : 50);

  const clampedHoverLeft = Math.min(92, Math.max(8, hoverLeft));

  // §48.4: events inside the slice — the lane's second figure. Sums the
  // histogram buckets the brush overlaps (server or client buckets alike).
  const brushSliceCount = useMemo(() => {
    if (!brush || !histogram || max <= min) return null;
    const start = histogram.fromServer ? serverTimeline.start : min;
    let total = 0;
    for (let i = 0; i < histogram.bucketCount; i += 1) {
      const bStart = start + i * histogram.bucketSpan;
      const bEnd = bStart + histogram.bucketSpan;
      if (bStart < brush.to && bEnd > brush.from) total += histogram.buckets[i];
    }
    return total;
  }, [brush, histogram, max, min, serverTimeline]);

  // §48.5: one roll step, shared by the roll effect (commit cadence) and the
  // render (glide duration). The brush GRAPHIC transitions `left` over exactly
  // one step while playing, so the browser interpolates between the discrete
  // bucket commits — smooth motion with zero animation frames, and the scenes
  // still see only stepped updates.
  const rollGlide = useMemo(() => {
    if (max <= min) return { stepSpan: 0, stepMs: 0 };
    const stepSpan =
      histogram?.bucketSpan && histogram.bucketSpan > 0
        ? histogram.bucketSpan
        : (max - min) / TIMELINE_HISTOGRAM_BUCKETS;
    const stepMs = Math.max(
      40,
      (TIMELINE_PLAYBACK_DURATION_MS / playbackSpeed) *
        (stepSpan / (max - min)),
    );
    return { stepSpan, stepMs };
  }, [histogram, max, min, playbackSpeed]);
  const magnifiedTooltip =
    (scrubPercent !== null || isPlaying) && Boolean(activeTimelineEvent);

  // In histogram mode, the hover shows the bucket under the cursor (or playhead),
  // not a single event. Derive it from the hover/playback position.
  // §48.4: hover-only — the floating card no longer follows playback (it
  // occluded the transport buttons); the BRUSH READOUT LANE above the
  // strip is the always-visible readout now.
  const bucketHoverPercent = scrubPercent;
  const hoveredBucket =
    useHistogram && bucketHoverPercent !== null && bucketBreakdowns.length
      ? bucketBreakdowns[
          Math.min(
            bucketBreakdowns.length - 1,
            Math.max(
              0,
              Math.floor((bucketHoverPercent / 100) * bucketBreakdowns.length),
            ),
          )
        ]
      : null;

  // The strip is the only thing that knows the LOADED SPAN, so it reports it;
  // the provider re-clamps the brush against it (a brush may never dangle
  // outside the data). Idempotent, so re-reporting on every render is free.
  // Deliberately NOT cleared on unmount: the brush outlives this component
  // (cross-page continuity), and the next strip reports its own span.
  useEffect(() => {
    if (!hasTimelineEvents || max <= min) return;
    setBrushWindow(min, max);
  }, [hasTimelineEvents, max, min, setBrushWindow]);

  // The band this strip occupies is not a constant: the range row wraps at
  // narrow widths and the readout lane appears with the brush. Measured here
  // because only the strip knows its own box — a consumer that hard-codes the
  // number parks scene content underneath it.
  useEffect(() => {
    if (!onHeightChange) return undefined;
    const el = stripRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const measure = () =>
      onHeightChange(
        el.getBoundingClientRect().height +
          (readoutVisible ? BRUSH_READOUT_BAND : 0),
      );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeightChange, readoutVisible]);

  // A page-driven STOP (the map's stopTimelinePlayback) clears the slice — it
  // is an explicit "show me everything again", not a pause. A window change
  // only stops the roll: the brush re-clamps in the provider and survives if it
  // still has data under it. `events` is deliberately absent — a refetch must
  // not kill a roll or drop the user's slice.
  const clearedRef = useRef(playbackStopSignal);
  useEffect(() => {
    if (clearedRef.current === playbackStopSignal) return;
    clearedRef.current = playbackStopSignal;
    setIsPlaying(false);
    clearBrush();
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
  }, [playbackStopSignal, clearBrush, setIsPlaying]);

  useEffect(() => {
    setIsPlaying(false);
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
  }, [timeRange, setIsPlaying]);

  // ── PLAY = ROLL (§48.1 ruling) ────────────────────────────────────────────
  // The fixed-width brush glides across the whole loaded span: a time-lapse of
  // the window, not a sweep inside the brush. Frames are scheduled ONLY from
  // here and ONLY while `isPlaying` — pause, unmount, a manual drag (which sets
  // isPlaying false) and a window change all land in this effect's cleanup.
  // Velocity is constant: TIMELINE_PLAYBACK_DURATION_MS buys one full traversal
  // of the span at 1×, so resuming mid-span takes proportionally less time.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const start = brushRef.current;
    if (!hasTimelineEvents || max <= min || !start) {
      setIsPlaying(false);
      return undefined;
    }

    const width = Math.max(0, Math.min(start.to - start.from, max - min));
    const endFrom = max - width;
    // At (or past) the end, play RESTARTS from the head — loop-at-end stops, so
    // the only way to re-roll a finished brush is another press of play.
    const startFrom = start.from >= endFrom ? min : start.from;
    const distance = endFrom - startFrom;
    if (distance <= 0) {
      setIsPlaying(false);
      return undefined;
    }

    // §48.4 ruling (stutter fix): the roll STEPS one histogram bucket per
    // tick instead of gliding per animation frame. A continuous roll
    // committed provider state ~60×/s — every commit re-rendered every
    // lens consumer and re-sliced every scene, and the cost spiked with
    // data density under the brush (the "pause on some plays"). Stepping
    // makes scene updates discrete, predictable, and bucket-aligned with
    // the histogram the user is watching. Timer-driven: no animation
    // frames at all — the scenes' own prop-change redraws are the frames.
    const { stepSpan, stepMs } = rollGlide;
    if (stepSpan <= 0) {
      setIsPlaying(false);
      return undefined;
    }
    let from = startFrom;
    const step = () => {
      from = Math.min(from + stepSpan, endFrom);
      setBrush({ from, to: from + width });
      if (from >= endFrom) {
        playbackFrameRef.current = null;
        setIsPlaying(false); // no auto-loop in v1
        return;
      }
      playbackFrameRef.current = window.setTimeout(step, stepMs);
    };
    playbackFrameRef.current = window.setTimeout(step, stepMs);

    return () => {
      if (playbackFrameRef.current) {
        window.clearTimeout(playbackFrameRef.current);
        playbackFrameRef.current = null;
      }
    };
  }, [
    hasTimelineEvents,
    isPlaying,
    max,
    min,
    rollGlide,
    setBrush,
    setIsPlaying,
  ]);

  // The page's own playback echo (the map's current-event highlight). The
  // PROVIDER is written by the brush setters above, not from here — this report
  // is page data only, and `playheadTimestamp` is the brush's leading edge so a
  // pre-C consumer sees exactly what it saw before.
  useEffect(() => {
    onPlaybackChange?.({
      brush,
      currentEventId: currentPlaybackEventId || null,
      isActive: playheadTimestamp !== null,
      isPlaying,
      playheadTimestamp,
    });
  }, [
    brush,
    currentPlaybackEventId,
    isPlaying,
    onPlaybackChange,
    playheadTimestamp,
  ]);

  const resetPlayback = () => {
    setIsPlaying(false);
    clearBrush();
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
  };

  // A brush of the default width centred on a point, kept inside the span.
  const brushAround = useCallback(
    (centerTs, widthTs) => {
      const width = Math.min(widthTs, span);
      const from = Math.max(min, Math.min(centerTs - width / 2, max - width));
      return { from, to: from + width };
    },
    [max, min, span],
  );

  const togglePlayback = () => {
    if (!hasTimelineEvents) return;
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    // Play with no slice: roll a default-width brush from the head of the span.
    if (!brush) {
      const width = (span * BRUSH_DEFAULT_PCT) / 100;
      setBrush({ from: min, to: min + width });
    }
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
    setIsPlaying(true);
  };

  // ── brush gestures ────────────────────────────────────────────────────────
  // One drag record, one pointer, in a ref: a pointermove must not re-render
  // anything but the brush itself.
  const dragRef = useRef(null);

  const percentAt = (clientX) => {
    const rect = timelineTrackRef.current?.getBoundingClientRect();
    if (!rect?.width) return null;
    return Math.min(
      100,
      Math.max(0, ((clientX - rect.left) / rect.width) * 100),
    );
  };

  const beginDrag = (event, mode, base) => {
    const pct = percentAt(event.clientX);
    if (pct === null) return;
    // Dragging while playing PAUSES: the hand wins over the roll.
    setIsPlaying(false);
    dragRef.current = {
      mode,
      grabTs: tsOfPct(pct),
      from: base.from,
      to: base.to,
    };
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
    try {
      timelineTrackRef.current?.setPointerCapture(event.pointerId);
    } catch {
      /* capture unsupported: the track's own move/up handlers still fire */
    }
  };

  // Track background: with no slice this CREATES one (default width, centred on
  // the click); with a slice it re-centres it there. Either way the same
  // gesture continues as a body drag.
  const handleTrackPointerDown = (event) => {
    if (timelineDisabled) return;
    const pct = percentAt(event.clientX);
    if (pct === null) return;
    const width = brush
      ? brush.to - brush.from
      : (span * BRUSH_DEFAULT_PCT) / 100;
    const next = brushAround(tsOfPct(pct), width);
    setBrush(next);
    beginDrag(event, "move", next);
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const pct = percentAt(event.clientX);
    if (pct === null) return;

    if (!drag) {
      // No drag: hover preview only (tooltip + magnifier), never a lens write.
      if (timelineDisabled) return;
      setScrubPercent(pct);
      setHoveredTimelineEvent(nearestEventForPercent(pct));
      return;
    }

    const minWidth = (span * BRUSH_MIN_PCT) / 100;
    const at = tsOfPct(pct);

    if (drag.mode === "move") {
      const width = drag.to - drag.from;
      const from = Math.max(
        min,
        Math.min(drag.from + (at - drag.grabTs), max - width),
      );
      setBrush({ from, to: from + width });
      return;
    }
    if (drag.mode === "from") {
      setBrush({
        from: Math.max(min, Math.min(at, drag.to - minWidth)),
        to: drag.to,
      });
      return;
    }
    setBrush({
      from: drag.from,
      to: Math.min(max, Math.max(at, drag.from + minWidth)),
    });
  };

  const endDrag = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (event?.pointerId == null) return;
    try {
      timelineTrackRef.current?.releasePointerCapture(event.pointerId);
    } catch {
      /* already released with the pointer */
    }
  };

  const clearScrub = () => {
    setScrubPercent(null);
    setHoveredTimelineEvent(null);
  };

  // Leaving the track ends the HOVER, never the drag: while the pointer is
  // captured the gesture owns the pointer wherever it goes.
  const handlePointerLeave = () => {
    if (dragRef.current) return;
    clearScrub();
  };

  const handlePointerUp = (event) => {
    endDrag(event);
  };

  const handlePointerCancel = (event) => {
    endDrag(event);
    clearScrub();
  };

  return (
    <>
      <style>{`
        @keyframes arwes-timeline-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.06),
              0 0 12px 2px rgba(${cyan},0.08),
              0 0 28px 3px rgba(${cyan},0.03),
              0 22px 48px rgba(0,0,0,0.35);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.12),
              0 0 16px 3px rgba(${cyan},0.13),
              0 0 40px 5px rgba(${cyan},0.05),
              0 22px 48px rgba(0,0,0,0.35);
          }
        }
      `}</style>

      <div
        ref={stripRef}
        className="pointer-events-none absolute inset-x-0 z-[990] px-4"
        style={{ bottom: `${bottomOffset}px` }}
      >
        <div
          className={[
            "pointer-events-auto relative mx-auto w-full max-w-[860px]",
            "rounded-xl border border-mainDivider bg-mainBg/95 p-2 font-poppins font-normal text-mainText backdrop-blur-md",
          ].join(" ")}
          style={{
            // backgroundImage, NOT the `background` shorthand: the shorthand
            // resets background-color, which was killing bg-mainBg/95's base
            // tint and leaving the glass as blur-only — the "washed out"
            // timeline bar from the prod-blocking feedback. Same fix as
            // LensToolbar; PanelFrame always did it correctly.
            backgroundImage:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 100%)",
            animation: "arwes-timeline-pulse 5s ease-in-out infinite",
            transition:
              "border-color 0.25s ease-out, background 0.25s ease-out, box-shadow 0.25s ease-out",
          }}
        >
          {/* §48.5: BRUSH READOUT — floats ABOVE the strip, outside its
              border, occupying no flow height and taking no pointer events,
              so it can occlude nothing interactive. Tracks the brush midpoint
              (clamped) and glides with the same transition as the brush. */}
          {readoutVisible && (
            <div className="pointer-events-none absolute inset-x-2 bottom-full mb-2 h-5">
              <span
                className="absolute top-0 inline-flex max-w-[60%] -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-md border border-velBlue/30 bg-mainBg/95 px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] text-velBlue shadow-lg shadow-black/30 backdrop-blur-md"
                style={{
                  left: `${Math.min(
                    88,
                    Math.max(12, (brushFromPct + brushToPct) / 2),
                  )}%`,
                  transition: isPlaying
                    ? `left ${rollGlide.stepMs}ms linear`
                    : "none",
                }}
              >
                <span className="normal-case tracking-normal">
                  {formatBucketRange(brush.from, brush.to)}
                </span>
                {brushSliceCount !== null && (
                  <span className="text-mutedText normal-case tracking-normal">
                    {brushSliceCount}{" "}
                    {brushSliceCount === 1 ? "event" : "events"}
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 px-1 text-[10px] uppercase tracking-[0.24em] text-mutedText">
            <span className="inline-flex min-w-0 items-center gap-2 text-mainText/85">
              <span>Timeline</span>
              {/* §48.4: the slice chip moved — the readout LANE above the
                  strip is the one always-visible slice display. */}
            </span>

            <div className="flex items-center gap-2">
              {/* T1: playback transport — play/pause, reset, and speed.
                  `showPlayback: false` keeps only the clear-slice button. */}
              <div className="flex items-center overflow-hidden rounded-lg border border-mainDivider bg-mainButtonBg/35 normal-case tracking-normal">
                {showPlayback && (
                  <>
                    <button
                      type="button"
                      className="inline-flex h-7 w-7 items-center justify-center text-mainText transition-all duration-200 hover:bg-mainButtonBg/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={togglePlayback}
                      disabled={timelineDisabled}
                      aria-label={
                        isPlaying
                          ? "Pause the timeline roll"
                          : "Roll the timeline brush"
                      }
                      title={isPlaying ? "Pause" : "Play (roll the brush)"}
                    >
                      {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                    </button>

                    <div className="h-4 w-px bg-mainDivider" />
                  </>
                )}

                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={resetPlayback}
                  disabled={!brush && !isPlaying}
                  aria-label="Clear the time slice"
                  title="Clear slice (show the whole window)"
                >
                  <RotateCcw size={11} />
                </button>

                {showPlayback && (
                  <>
                    <div className="h-4 w-px bg-mainDivider" />

                    <button
                      type="button"
                      className="inline-flex h-7 min-w-[2rem] items-center justify-center px-1.5 text-[10px] font-semibold text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 disabled:cursor-not-allowed disabled:opacity-40"
                      onClick={() =>
                        setPlaybackSpeed((current) => {
                          const index =
                            TIMELINE_PLAYBACK_SPEEDS.indexOf(current);
                          return TIMELINE_PLAYBACK_SPEEDS[
                            (index + 1) % TIMELINE_PLAYBACK_SPEEDS.length
                          ];
                        })
                      }
                      disabled={timelineDisabled}
                      aria-label={`Roll speed ${playbackSpeed}×`}
                      title="Roll speed"
                    >
                      {playbackSpeed}×
                    </button>
                  </>
                )}
              </div>

              {rangeLocked ? (
                <div
                  className="flex items-center gap-1.5 rounded-lg border border-velBlue/30 bg-velBlue/10 px-2.5 py-1 text-[10px] text-velBlue"
                  title="This view fixes the time range"
                >
                  <Lock size={10} />
                  {rangeLabel(timeRange)}
                </div>
              ) : (
                <div className="relative flex items-center gap-1 rounded-lg border border-mainDivider bg-mainButtonBg/35 p-0.5 normal-case tracking-normal">
                  {TIME_RANGES.filter((range) =>
                    TIMELINE_RANGE_KEYS.includes(range.key),
                  ).map((range) => {
                    const active = range.key === timeRange;

                    return (
                      <button
                        key={range.key}
                        type="button"
                        className={[
                          "rounded-md px-2.5 py-1 text-[10px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                          active
                            ? "bg-velBlue/15 text-velBlue shadow-[0_0_14px_rgba(14,165,233,0.18)]"
                            : "text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText",
                        ].join(" ")}
                        onClick={() => {
                          resetPlayback();
                          onTimeRangeChange(range.key);
                        }}
                      >
                        {range.label}
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className={[
                      "rounded-md px-2.5 py-1 text-[10px] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                      timeRange === "custom"
                        ? "bg-velBlue/15 text-velBlue shadow-[0_0_14px_rgba(14,165,233,0.18)]"
                        : "text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText",
                    ].join(" ")}
                    // Keep this mousedown from reaching the document-level
                    // outside-click closer, which would close-then-reopen.
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => {
                      setCustomOpen((current) => {
                        const next = !current;
                        if (next) {
                          setDraftFrom(customRange?.from || isoDateDaysAgo(7));
                          setDraftTo(customRange?.to || isoDateDaysAgo(0));
                        }
                        return next;
                      });
                    }}
                  >
                    {timeRange === "custom" && customRange
                      ? `${shortRangeDate(customRange.from)} – ${shortRangeDate(
                          customRange.to,
                        )}`
                      : "Custom"}
                  </button>

                  {customOpen && (
                    <div
                      ref={customPickerRef}
                      className="absolute bottom-full right-0 z-[40] mb-2 w-64 rounded-xl border border-mainDivider bg-mainBg/95 p-3 text-left shadow-[0_18px_44px_rgba(0,0,0,0.45)] normal-case tracking-normal backdrop-blur-md"
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-mutedText">
                        Custom range
                      </div>

                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <label className="block">
                          <span className="mb-1 block text-[9px] uppercase tracking-[0.12em] text-mutedText/75">
                            From
                          </span>
                          <input
                            type="date"
                            value={draftFrom}
                            max={draftTo || undefined}
                            onChange={(event) =>
                              setDraftFrom(event.target.value)
                            }
                            className="w-full rounded-md border border-mainDivider bg-mainButtonBg/40 px-2 py-1.5 text-[11px] text-mainText focus:outline-none focus:ring-2 focus:ring-inputFocus/60"
                            style={{ colorScheme: dark ? "dark" : "light" }}
                          />
                        </label>

                        <label className="block">
                          <span className="mb-1 block text-[9px] uppercase tracking-[0.12em] text-mutedText/75">
                            To
                          </span>
                          <input
                            type="date"
                            value={draftTo}
                            min={draftFrom || undefined}
                            onChange={(event) => setDraftTo(event.target.value)}
                            className="w-full rounded-md border border-mainDivider bg-mainButtonBg/40 px-2 py-1.5 text-[11px] text-mainText focus:outline-none focus:ring-2 focus:ring-inputFocus/60"
                            style={{ colorScheme: dark ? "dark" : "light" }}
                          />
                        </label>
                      </div>

                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          className="rounded-md px-2.5 py-1.5 text-[10px] uppercase tracking-[0.12em] text-mutedText transition hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                          onClick={() => setCustomOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={
                            !draftFrom || !draftTo || draftTo < draftFrom
                          }
                          className="rounded-md bg-velBlue/15 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-velBlue transition hover:bg-velBlue/25 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                          onClick={() => {
                            resetPlayback();
                            onCustomRangeApply?.({
                              from: draftFrom,
                              to: draftTo,
                            });
                            setCustomOpen(false);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="relative h-7 select-none rounded-lg border border-mainDivider bg-mainButtonBg/30 px-3">
            <div
              ref={timelineTrackRef}
              className={`absolute bottom-0 left-3 right-3 top-0 touch-none ${
                timelineDisabled ? "cursor-not-allowed" : "cursor-crosshair"
              }`}
              onPointerCancel={handlePointerCancel}
              onPointerDown={
                timelineDisabled ? undefined : handleTrackPointerDown
              }
              onPointerLeave={handlePointerLeave}
              onPointerMove={timelineDisabled ? undefined : handlePointerMove}
              onPointerUp={handlePointerUp}
            >
              {useHistogram ? (
                hoveredBucket && hoveredBucket.total > 0 ? (
                  <TimelineBucketTooltip
                    bucket={hoveredBucket}
                    left={clampedHoverLeft}
                  />
                ) : null
              ) : (
                <TimelineHoverTooltip
                  event={activeTimelineEvent}
                  left={clampedHoverLeft}
                  magnified={magnifiedTooltip}
                />
              )}

              {/* Dot-mode axis (low density). The old left-to-playhead progress
                  fill is gone: the BRUSH is the position indicator now, and a
                  fill from the origin would contradict a slice that starts
                  mid-span. */}
              {!useHistogram && (
                <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-mainDivider" />
              )}

              {/* Histogram-mode baseline + density bars (T3, dense windows). */}
              {useHistogram && (
                <div className="absolute inset-x-0 bottom-0 h-px bg-mainDivider" />
              )}

              {useHistogram &&
                histogram.buckets.map((count, index) => {
                  if (!count) return null;
                  const leftPct = (index / histogram.bucketCount) * 100;
                  const widthPct = 100 / histogram.bucketCount;
                  const heightPct = Math.max(
                    12,
                    (count / histogram.maxCount) * 100,
                  );
                  const bucketMid = min + (index + 0.5) * histogram.bucketSpan;
                  // Outside the brush = outside the visible slice.
                  const isFuture = !inSlice(bucketMid);
                  return (
                    <div
                      key={index}
                      aria-hidden
                      className="pointer-events-none absolute bottom-0"
                      style={{
                        left: `${leftPct}%`,
                        width: `calc(${widthPct}% - 2px)`,
                        height: `${heightPct}%`,
                      }}
                    >
                      <div
                        className={`h-full w-full rounded-t-[3px] transition-opacity duration-200 ${
                          isFuture ? "bg-mutedText/40" : "bg-velBlue/70"
                        }`}
                      />
                    </div>
                  );
                })}

              {/* Hover hairline — a PREVIEW of where a click would land. It is
                  not the playhead: the brush's leading edge is. */}
              {!timelineDisabled && scrubPercent !== null && !brush && (
                <div
                  aria-hidden
                  className="absolute top-1/2 h-5 w-px -translate-y-1/2 bg-velBlue/40"
                  style={{ left: `${scrubPercent}%` }}
                />
              )}

              {/* ── THE BRUSH (§48.1) ────────────────────────────────────────
                  The visible time slice, and the playhead generalized to a
                  range. Body drags it, the edge grips resize it, the × returns
                  to the whole window. Sits BELOW the dot buttons (z-[5] vs
                  their 10+) so dot selection still works in low-density mode. */}
              {!timelineDisabled && brush && (
                <div
                  className="absolute inset-y-0 z-[5]"
                  style={{
                    left: `${brushFromPct}%`,
                    width: `${Math.max(0, brushToPct - brushFromPct)}%`,
                    // §48.5 hybrid glide: while ROLLING, `left` transitions
                    // over exactly one step so the browser interpolates
                    // between the discrete bucket commits — constant-velocity
                    // motion, zero animation frames. Never during a drag
                    // (drag sets isPlaying false → position is instant under
                    // the pointer). Restart-from-end reads as a quick rewind.
                    transition: isPlaying
                      ? `left ${rollGlide.stepMs}ms linear`
                      : "none",
                  }}
                >
                  <div
                    role="presentation"
                    aria-label="Time slice — drag to move"
                    title="Drag to move the slice · drag an edge to resize"
                    onPointerDown={(event) => {
                      event.stopPropagation();
                      beginDrag(event, "move", brush);
                    }}
                    className={`absolute inset-0 rounded-[3px] border-x border-velBlue/60 bg-velBlue/12 shadow-[inset_0_0_18px_rgba(14,165,233,0.15)] ${
                      isPlaying
                        ? "cursor-progress"
                        : "cursor-grab active:cursor-grabbing"
                    }`}
                  />

                  {[
                    { mode: "from", side: "left", label: "Slice start" },
                    { mode: "to", side: "right", label: "Slice end" },
                  ].map((grip) => (
                    <div
                      key={grip.mode}
                      role="presentation"
                      aria-label={`${grip.label} — drag to resize`}
                      title={grip.label}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                        beginDrag(event, grip.mode, brush);
                      }}
                      className="absolute inset-y-0 flex cursor-ew-resize items-center justify-center"
                      style={{
                        [grip.side]: `-${BRUSH_GRIP_PX / 2}px`,
                        width: `${BRUSH_GRIP_PX}px`,
                      }}
                    >
                      <span className="h-4 w-[3px] rounded-full bg-velBlue/80 shadow-[0_0_10px_rgba(14,165,233,0.45)]" />
                    </div>
                  ))}

                  {/* Clear affordance — rides the slice's trailing edge so it is
                      reachable at any width, including the 2% floor. */}
                  <button
                    type="button"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      resetPlayback();
                    }}
                    aria-label="Clear the time slice"
                    title="Clear slice (show the whole window)"
                    className="absolute left-full top-0 z-[6] -translate-x-1/2 -translate-y-1/2 rounded-full border border-velBlue/50 bg-mainBg p-[2px] text-velBlue transition-colors duration-150 hover:bg-velBlue/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                  >
                    <X size={8} />
                  </button>
                </div>
              )}

              {!useHistogram &&
                renderedTimelineEvents.map((event) => {
                  const id = getEventId(event);
                  const left = eventLeft(event);
                  const active = id === selectedId;
                  const properties = event.properties || {};
                  const activePercent = scrubPercent ?? playbackPercent;
                  const distanceFromScrub =
                    activePercent === null
                      ? Number.POSITIVE_INFINITY
                      : Math.abs(left - activePercent);
                  const magnifyWeight = Math.max(
                    0,
                    1 - distanceFromScrub / TIMELINE_MAGNIFY_RADIUS,
                  );
                  const selectedForScrub =
                    activeTimelineEventId && id === activeTimelineEventId;
                  // "future" is now "outside the slice" — the brush is the
                  // window, so anything it does not cover is dimmed, on either
                  // side of it.
                  const playbackEventState = !brush
                    ? "idle"
                    : selectedForScrub
                      ? "current"
                      : inSlice(properties.timestamp)
                        ? "past"
                        : "future";
                  const dotScale = selectedForScrub
                    ? 1.28
                    : 1 + magnifyWeight * TIMELINE_MAGNIFY_BOOST * 0.32;
                  const dotTheme =
                    playbackEventState === "future"
                      ? timelineDotTheme.future
                      : timelineDotTheme.current;

                  return (
                    <button
                      key={id}
                      type="button"
                      title={`${displayText(
                        properties.title,
                        "Untitled event",
                      )} · ${formatTimestamp(properties.timestamp)}`}
                      className={[
                        "absolute top-1/2 flex h-2.5 w-2.5 items-center justify-center rounded-full border border-mainBg transition-[box-shadow,opacity,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                        playbackEventState === "past"
                          ? "opacity-65"
                          : playbackEventState === "future"
                            ? "opacity-40"
                            : "opacity-95",
                        active || selectedForScrub
                          ? "shadow-[0_0_0_1px_rgba(255,255,255,0.55),0_0_10px_rgba(14,165,233,0.22)]"
                          : "hover:opacity-100 hover:shadow-[0_0_12px_rgba(14,165,233,0.22)]",
                      ].join(" ")}
                      style={{
                        left: `${left}%`,
                        transform: `translate(-50%, -50%) scale(${dotScale})`,
                        transformOrigin: "center",
                        zIndex: selectedForScrub
                          ? 90
                          : 10 +
                            Math.round(magnifyWeight * 30) +
                            (active ? 40 : 0),
                      }}
                      onClick={() => {
                        // Selecting a dot parks the slice on it (existing width,
                        // or the default when there is none) — the click reads as
                        // "show me around this".
                        setIsPlaying(false);
                        const at = properties.timestamp;
                        if (Number.isFinite(at)) {
                          setBrush(
                            brushAround(
                              at,
                              brush
                                ? brush.to - brush.from
                                : (span * BRUSH_DEFAULT_PCT) / 100,
                            ),
                          );
                        }
                        onSelectEvent(event);
                      }}
                      onFocus={() => setHoveredTimelineEvent(event)}
                      onBlur={clearScrub}
                    >
                      <span
                        className={`relative z-10 h-1.5 w-1.5 rounded-full ${dotTheme}`}
                      />
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="mt-1 flex justify-between text-[10px] text-mutedText">
            <span>{timelineDisabled ? "—" : formatTimestamp(min)}</span>
            <span>{timelineDisabled ? "—" : formatTimestamp(max)}</span>
          </div>
        </div>
      </div>
    </>
  );
}
